import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import { DEFAULT_SETTINGS, Settings } from '@/models/settings';
import { Pagination } from '@/models';
import { prepareHeaders } from '@/state/slices/index';

type SettingsApi = Omit<Settings, 'showAddendumModal'> & {
  show_addendum_modal?: boolean;
  showAddendumModal?: boolean;
};

function mapSettingFromApi(setting: SettingsApi): Settings {
  return {
    ...setting,
    showAddendumModal:
      setting.showAddendumModal ??
      setting.show_addendum_modal ??
      DEFAULT_SETTINGS.showAddendumModal,
  };
}

function mapSettingsPageFromApi(
  page: Pagination<SettingsApi>
): Pagination<Settings> {
  return {
    ...page,
    results: page.results.map(mapSettingFromApi),
  };
}

function mapSettingPatchToApi(data: Partial<Settings>) {
  const patchData: Record<string, unknown> = { ...data };

  if ('showAddendumModal' in patchData) {
    patchData.show_addendum_modal = patchData.showAddendumModal;
    delete patchData.showAddendumModal;
  }

  return patchData;
}

export const SettingsAPI = createApi({
  reducerPath: 'SettingsAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['Setting'],
  endpoints: (builder) => ({
    getSettings: builder.query<Pagination<Settings>, void>({
      query: () => ({
        url: '/settings/',
        method: 'GET',
      }),
      transformResponse: (response: Pagination<SettingsApi>) =>
        mapSettingsPageFromApi(response),
      providesTags: (result) =>
        result
          ? [
              { type: 'Setting', id: 'LIST' },
              ...result.results.map((s) => ({
                type: 'Setting' as const,
                id: s.uuid,
              })),
            ]
          : [{ type: 'Setting', id: 'LIST' }],
    }),
    patchSetting: builder.mutation<
      Settings,
      { uuid: string; data: Partial<Settings> }
    >({
      query: ({ uuid, data }) => ({
        url: `/settings/${uuid}/`,
        method: 'PATCH',
        body: mapSettingPatchToApi(data),
      }),
      transformResponse: (response: SettingsApi) => mapSettingFromApi(response),
      // No tag invalidation — local state is the source of truth after
      // initial load. We don't want a refetch overwriting user changes.
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useLazyGetSettingsQuery,
  usePatchSettingMutation,
} = SettingsAPI;
